/**
 * Accreditation Decision Panel
 * Full governance decision engine for a single course accreditation.
 * Covers: decision recording, conditional approval, rejection, revocation,
 *         condition resolution, and full audit trail.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader, StandardModal, StatusBadge, Alert, Btn,
  FormField, Card, inputStyle, textareaStyle, selectStyle,
  colors, spacing, typography, radius, shadows,
} from "../design-system";

const BASE    = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const ACC_API = `${BASE}/course-accreditations`;

// ── Constants ─────────────────────────────────────────────────────────────────

const DECISION_OPTIONS = [
  { value: "approve",             label: "Approve — Full Accreditation" },
  { value: "conditional_approve", label: "Conditional Approval — with Conditions" },
  { value: "reject",              label: "Reject Application" },
  { value: "suspend",             label: "Suspend Accreditation" },
  { value: "revoke",              label: "Revoke Accreditation" },
  { value: "reinstate",           label: "Reinstate (from Suspended)" },
  { value: "note",                label: "Committee Note (no status change)" },
];

const ALLOWED_FROM = {
  approve:             ["pending", "conditional"],
  conditional_approve: ["pending"],
  reject:              ["pending", "conditional"],
  suspend:             ["approved", "conditional"],
  revoke:              ["pending", "approved", "suspended", "conditional"],
  reinstate:           ["suspended"],
  note:                ["pending","approved","suspended","revoked","conditional","expired"],
};

const DECISION_COLORS = {
  approve:             colors.success,
  conditional_approve: colors.warning,
  reject:              colors.danger,
  suspend:             colors.warning,
  revoke:              colors.danger,
  reinstate:           colors.success,
  condition_resolved:  colors.success,
  note:                colors.info,
};

const DECISION_LABELS = {
  approve:             "Approved",
  conditional_approve: "Conditionally Approved",
  reject:              "Rejected",
  suspend:             "Suspended",
  revoke:              "Revoked",
  reinstate:           "Reinstated",
  condition_resolved:  "Condition Resolved",
  note:                "Committee Note",
};

const STATUS_GUIDANCE = {
  pending:     "This accreditation is awaiting a committee decision. Review evidence and record a formal decision below.",
  conditional: "Accreditation is conditionally approved. Outstanding conditions must be resolved before full approval is granted.",
  approved:    "Accreditation is active and fully approved. Available actions: suspend or revoke.",
  suspended:   "Accreditation is suspended. The provider cannot deliver the course until reinstated.",
  revoked:     "Accreditation has been revoked. A new application must be submitted to re-accredit.",
  expired:     "Accreditation has expired. Renewal is required.",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  const map = {
    pending:     { bg: colors.warningLight,  text: colors.warning, label: "Pending" },
    conditional: { bg: "#fef3c7",            text: "#b45309",      label: "Conditional" },
    approved:    { bg: colors.successLight,  text: colors.success, label: "Approved" },
    suspended:   { bg: colors.warningLight,  text: colors.warning, label: "Suspended" },
    revoked:     { bg: colors.dangerLight,   text: colors.danger,  label: "Revoked" },
    expired:     { bg: colors.backgroundAlt, text: colors.textMuted, label: "Expired" },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      background: s.bg, color: s.text,
      border: `1px solid ${s.text}44`,
      borderRadius: radius.pill,
      padding: "4px 14px",
      fontSize: typography.fontSizes.sm,
      fontWeight: typography.fontWeights.semibold,
    }}>{s.label}</span>
  );
}

function TimelineEntry({ entry, isLast }) {
  const color = DECISION_COLORS[entry.decision_type] || colors.textMuted;
  return (
    <div style={{ display: "flex", gap: spacing.md, position: "relative" }}>
      {/* Spine */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: color + "18",
          border: `2px solid ${color}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "14px", flexShrink: 0,
        }}>
          {entry.decision_type === "approve" ? "✓" :
           entry.decision_type === "conditional_approve" ? "◑" :
           entry.decision_type === "reject" ? "✕" :
           entry.decision_type === "suspend" ? "⏸" :
           entry.decision_type === "revoke" ? "⊗" :
           entry.decision_type === "reinstate" ? "↺" :
           entry.decision_type === "condition_resolved" ? "✓" : "✎"}
        </div>
        {!isLast && <div style={{ width: 2, flex: 1, background: colors.border, marginTop: 4, minHeight: 24 }} />}
      </div>
      {/* Content */}
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : spacing.lg }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 4 }}>
          <span style={{ fontWeight: typography.fontWeights.semibold, color: color, fontSize: typography.fontSizes.md }}>
            {DECISION_LABELS[entry.decision_type] || entry.decision_type}
          </span>
          <span style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
            {timeAgo(entry.decided_at)} · {entry.decided_by_name || "System"}
          </span>
        </div>
        {entry.previous_status && entry.new_status && entry.previous_status !== entry.new_status && (
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, margin: "2px 0" }}>
            <span style={{ background: colors.backgroundAlt, padding: "1px 7px", borderRadius: 4 }}>{entry.previous_status}</span>
            {" → "}
            <span style={{ background: colors.backgroundAlt, padding: "1px 7px", borderRadius: 4 }}>{entry.new_status}</span>
          </div>
        )}
        <p style={{ margin: "4px 0 0", fontSize: typography.fontSizes.sm, color: colors.textPrimary, lineHeight: 1.5 }}>
          {entry.rationale}
        </p>
        {entry.conditions && entry.conditions.length > 0 && (
          <div style={{ marginTop: spacing.xs }}>
            {entry.conditions.map((c, i) => (
              <div key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: colors.warningLight, color: colors.warning,
                border: `1px solid ${colors.warning}44`,
                borderRadius: radius.sm, padding: "1px 8px",
                fontSize: typography.fontSizes.xs, marginRight: 4, marginTop: 2,
              }}>
                ⚠ {c}
              </div>
            ))}
          </div>
        )}
        {entry.effective_from && (
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 4 }}>
            Effective: {fmtDate(entry.effective_from)}{entry.effective_until ? ` – ${fmtDate(entry.effective_until)}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AccreditationDecisionPanel() {
  const { accredId } = useParams();
  const navigate     = useNavigate();

  const [accred,    setAccred]    = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [showDecision,  setShowDecision]  = useState(false);
  const [showCondResolve, setShowCondResolve] = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [msg,           setMsg]           = useState("");
  const [msgType,       setMsgType]       = useState("success");

  // Decision form state
  const [decisionType,    setDecisionType]    = useState("");
  const [rationale,       setRationale]       = useState("");
  const [conditionInput,  setConditionInput]  = useState("");
  const [conditionsList,  setConditionsList]  = useState([]);
  const [effectiveFrom,   setEffectiveFrom]   = useState("");
  const [effectiveUntil,  setEffectiveUntil]  = useState("");

  // Condition resolve form state
  const [resolveItems,    setResolveItems]    = useState([]);
  const [resolveNotes,    setResolveNotes]    = useState("");

  const token   = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 4000); };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, dRes] = await Promise.all([
        fetch(`${ACC_API}/${accredId}`, { headers }),
        fetch(`${ACC_API}/${accredId}/decisions`, { headers }),
      ]);
      if (aRes.ok) setAccred(await aRes.json());
      if (dRes.ok) setDecisions(await dRes.json());
    } finally {
      setLoading(false);
    }
  }, [accredId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Compute available decision types for current status
  const availableDecisions = accred
    ? DECISION_OPTIONS.filter(d => (ALLOWED_FROM[d.value] || []).includes(accred.status))
    : [];

  function resetDecisionForm() {
    setDecisionType(""); setRationale("");
    setConditionInput(""); setConditionsList([]);
    setEffectiveFrom(""); setEffectiveUntil("");
  }

  function addCondition() {
    const c = conditionInput.trim();
    if (c && !conditionsList.includes(c)) {
      setConditionsList(prev => [...prev, c]);
      setConditionInput("");
    }
  }

  function removeCondition(c) {
    setConditionsList(prev => prev.filter(x => x !== c));
  }

  async function submitDecision(e) {
    e.preventDefault();
    if (!decisionType) return flash("Please select a decision type.", "error");
    if (!rationale.trim()) return flash("Rationale is required.", "error");
    if (decisionType === "conditional_approve" && conditionsList.length === 0)
      return flash("Add at least one condition for conditional approval.", "error");

    setSaving(true);
    try {
      const body = {
        decision_type: decisionType,
        rationale: rationale.trim(),
        conditions: conditionsList.length > 0 ? conditionsList : null,
        effective_from: effectiveFrom || null,
        effective_until: effectiveUntil || null,
      };
      const res = await fetch(`${ACC_API}/${accredId}/decision`, {
        method: "POST", headers, body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || "Failed to record decision");
      }
      flash("Decision recorded successfully.");
      setShowDecision(false);
      resetDecisionForm();
      fetchData();
    } catch (err) {
      flash(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function submitResolveCondition(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        decision_type: "condition_resolved",
        rationale: resolveNotes.trim() || "Conditions resolved by committee.",
        conditions: resolveItems.length > 0 ? resolveItems : null,
      };
      const res = await fetch(`${ACC_API}/${accredId}/resolve-condition`, {
        method: "PATCH", headers, body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || "Failed");
      }
      flash("Conditions resolved. Accreditation status updated.");
      setShowCondResolve(false);
      setResolveItems([]); setResolveNotes("");
      fetchData();
    } catch (err) {
      flash(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  function toggleResolveItem(c) {
    setResolveItems(prev =>
      prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
        <div style={{ color: colors.textMuted }}>Loading decision panel…</div>
      </div>
    );
  }

  if (!accred) {
    return (
      <div style={{ padding: spacing.xl }}>
        <Alert type="error" message="Accreditation record not found." />
      </div>
    );
  }

  const guidance = STATUS_GUIDANCE[accred.status] || "";
  const hasConditions = accred.conditions && accred.conditions.length > 0;

  return (
    <div style={{ padding: `${spacing.lg} ${spacing.xl}`, maxWidth: 1100, margin: "0 auto" }}>

      {/* ── Header ── */}
      <PageHeader
        title="Accreditation Decision Panel"
        breadcrumb={
          <span>
            <button onClick={() => navigate("/accreditation/course-accreditations")}
              style={{ background: "none", border: "none", color: colors.primary, cursor: "pointer", padding: 0, fontSize: typography.fontSizes.sm }}>
              ← Course Accreditations
            </button>
          </span>
        }
        subtitle={`${accred.course_title || `Accreditation #${accred.id}`} — formal governance decisions`}
        actions={
          <div style={{ display: "flex", gap: spacing.sm }}>
            {accred.status === "conditional" && hasConditions && (
              <Btn variant="warning" size="sm" onClick={() => setShowCondResolve(true)}>
                Resolve Conditions
              </Btn>
            )}
            {availableDecisions.length > 0 && (
              <Btn variant="primary" onClick={() => setShowDecision(true)}>
                Record Decision
              </Btn>
            )}
          </div>
        }
      />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {/* ── Status banner ── */}
      <Card style={{ marginBottom: spacing.lg, background: colors.backgroundAlt }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.xl, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
              Current Status
            </div>
            <StatusChip status={accred.status} />
          </div>
          <div>
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Issued</div>
            <div style={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.sm }}>{fmtDate(accred.issued_at)}</div>
          </div>
          <div>
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Expires</div>
            <div style={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.sm }}>{fmtDate(accred.expires_at)}</div>
          </div>
          <div>
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Decisions recorded</div>
            <div style={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.sm }}>{decisions.length}</div>
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Guidance</div>
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, lineHeight: 1.5 }}>{guidance}</div>
          </div>
        </div>
      </Card>

      {/* ── Outstanding conditions ── */}
      {hasConditions && (
        <Card accent={colors.warning} style={{ marginBottom: spacing.lg }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing.md }}>
            <div style={{ fontWeight: typography.fontWeights.semibold, color: colors.warning }}>
              Outstanding Conditions ({accred.conditions.length})
            </div>
            <Btn variant="warning" size="sm" onClick={() => setShowCondResolve(true)}>
              Mark Resolved
            </Btn>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
            {accred.conditions.map((c, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: spacing.sm,
                background: colors.warningLight, borderRadius: radius.md,
                padding: `${spacing.xs} ${spacing.md}`,
                border: `1px solid ${colors.warning}44`,
              }}>
                <span style={{ color: colors.warning, fontWeight: "bold", flexShrink: 0 }}>⚠</span>
                <span style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>{c}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: spacing.sm, fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
            All conditions must be resolved before full accreditation status is granted.
            Resolving the final condition will automatically promote status to Approved.
          </div>
        </Card>
      )}

      {/* ── Two-column layout ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: spacing.xl, alignItems: "start" }}>

        {/* ── Decision Audit Trail ── */}
        <Card>
          <div style={{
            fontWeight: typography.fontWeights.semibold,
            fontSize: typography.fontSizes.lg,
            marginBottom: spacing.lg,
            paddingBottom: spacing.sm,
            borderBottom: `1px solid ${colors.border}`,
          }}>
            Decision Audit Trail
          </div>

          {decisions.length === 0 ? (
            <div style={{ textAlign: "center", padding: `${spacing.xl} 0`, color: colors.textMuted }}>
              <div aria-hidden="true" style={{ fontSize: "32px", marginBottom: spacing.sm }}>📋</div>
              <div style={{ fontSize: typography.fontSizes.md }}>No decisions recorded yet.</div>
              <div style={{ fontSize: typography.fontSizes.sm, marginTop: spacing.xs }}>
                Record the first committee decision using the button above.
              </div>
            </div>
          ) : (
            <div style={{ paddingTop: spacing.xs }}>
              {decisions.map((d, i) => (
                <TimelineEntry key={d.id} entry={d} isLast={i === decisions.length - 1} />
              ))}
            </div>
          )}
        </Card>

        {/* ── Workflow Guide ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
          <Card>
            <div style={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.md, marginBottom: spacing.md }}>
              Available Actions
            </div>
            {availableDecisions.length === 0 ? (
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>
                No actions available in current status.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                {availableDecisions.map(d => (
                  <button
                    key={d.value}
                    onClick={() => { setDecisionType(d.value); setShowDecision(true); }}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: `${spacing.sm} ${spacing.md}`,
                      background: colors.backgroundAlt,
                      border: `1px solid ${colors.border}`,
                      borderRadius: radius.md,
                      cursor: "pointer",
                      fontSize: typography.fontSizes.sm,
                      color: DECISION_COLORS[d.value] || colors.textPrimary,
                      fontWeight: typography.fontWeights.medium,
                      transition: "background 120ms",
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <div style={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.md, marginBottom: spacing.md }}>
              Decision Requirements
            </div>
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, lineHeight: 1.6 }}>
              <div style={{ marginBottom: spacing.xs }}>✓ A written rationale is required for every decision</div>
              <div style={{ marginBottom: spacing.xs }}>✓ Conditional approval requires at least one listed condition</div>
              <div style={{ marginBottom: spacing.xs }}>✓ Suspension and revocation require documented grounds</div>
              <div style={{ marginBottom: spacing.xs }}>✓ All decisions are immutable once recorded</div>
              <div>✓ Decisions form the auditable record for regulatory inspection</div>
            </div>
          </Card>

          <Card>
            <div style={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.md, marginBottom: spacing.md }}>
              Evidence
            </div>
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, marginBottom: spacing.sm }}>
              Review all uploaded evidence before recording a decision.
            </div>
            <Btn
              variant="secondary"
              size="sm"
              style={{ width: "100%" }}
              onClick={() => navigate(`/accreditation/course-accreditations/${accredId}/evidence`)}
            >
              View Evidence Pack →
            </Btn>
          </Card>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          RECORD DECISION MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {showDecision && (
        <StandardModal
          title="Record Committee Decision"
          onClose={() => { setShowDecision(false); resetDecisionForm(); }}
          width={620}
          footer={
            <>
              <Btn variant="secondary" onClick={() => { setShowDecision(false); resetDecisionForm(); }}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="decision-form" loading={saving}>Record Decision</Btn>
            </>
          }
        >
          <form id="decision-form" onSubmit={submitDecision}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>

              <FormField label="Decision Type" htmlFor="dt-type" required>
                <select
                  id="dt-type"
                  className="ds-input"
                  value={decisionType}
                  onChange={e => setDecisionType(e.target.value)}
                  required
                  style={selectStyle}
                >
                  <option value="">— Select decision type —</option>
                  {availableDecisions.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </FormField>

              {decisionType && (
                <div style={{
                  background: (DECISION_COLORS[decisionType] || colors.info) + "12",
                  border: `1px solid ${(DECISION_COLORS[decisionType] || colors.info)}44`,
                  borderRadius: radius.md,
                  padding: `${spacing.xs} ${spacing.md}`,
                  fontSize: typography.fontSizes.sm,
                  color: DECISION_COLORS[decisionType] || colors.textSecondary,
                }}>
                  {decisionType === "approve" && "Full accreditation will be granted. The course may be delivered from the decision date."}
                  {decisionType === "conditional_approve" && "Accreditation is granted subject to the listed conditions being met within the agreed timeframe."}
                  {decisionType === "reject" && "Application is refused. The provider may re-apply once the identified deficiencies have been addressed."}
                  {decisionType === "suspend" && "Accreditation is temporarily withdrawn. The provider must cease delivery until reinstatement."}
                  {decisionType === "revoke" && "Accreditation is permanently withdrawn. A new application is required for re-accreditation."}
                  {decisionType === "reinstate" && "Suspension is lifted. Full accreditation is restored with the original expiry date."}
                  {decisionType === "note" && "A committee note is recorded for audit purposes. No status change is applied."}
                </div>
              )}

              <FormField label="Decision Rationale" htmlFor="dt-rationale" required
                helper="Document the factual basis for this decision. This is an immutable audit record.">
                <textarea
                  id="dt-rationale"
                  className="ds-input"
                  value={rationale}
                  onChange={e => setRationale(e.target.value)}
                  required
                  rows={5}
                  placeholder="Provide full rationale including evidence reviewed, requirements met or failed, and committee agreement…"
                  style={textareaStyle}
                />
              </FormField>

              {decisionType === "conditional_approve" && (
                <FormField label="Conditions" htmlFor="dt-cond-input" required
                  helper="List each outstanding condition. Provider must satisfy all conditions before full approval is granted.">
                  <div style={{ display: "flex", gap: spacing.xs, marginBottom: spacing.xs }}>
                    <input
                      id="dt-cond-input"
                      className="ds-input"
                      type="text"
                      value={conditionInput}
                      onChange={e => setConditionInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCondition(); } }}
                      placeholder="Enter condition and press Enter or Add"
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <Btn variant="secondary" size="sm" type="button" onClick={addCondition}>Add</Btn>
                  </div>
                  {conditionsList.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {conditionsList.map((c, i) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          background: colors.warningLight,
                          border: `1px solid ${colors.warning}44`,
                          borderRadius: radius.sm, padding: "4px 10px",
                        }}>
                          <span style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>{c}</span>
                          <button type="button" onClick={() => removeCondition(c)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: colors.textMuted, padding: "0 0 0 8px" }}>
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </FormField>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md }}>
                <FormField label="Effective From" htmlFor="dt-eff-from"
                  helper="Leave blank to default to today.">
                  <input
                    id="dt-eff-from"
                    className="ds-input"
                    type="date"
                    value={effectiveFrom}
                    onChange={e => setEffectiveFrom(e.target.value)}
                    style={inputStyle}
                  />
                </FormField>
                <FormField label="Effective Until" htmlFor="dt-eff-until"
                  helper="Optional — leave blank for ongoing.">
                  <input
                    id="dt-eff-until"
                    className="ds-input"
                    type="date"
                    value={effectiveUntil}
                    onChange={e => setEffectiveUntil(e.target.value)}
                    style={inputStyle}
                  />
                </FormField>
              </div>
            </div>
          </form>
        </StandardModal>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          RESOLVE CONDITIONS MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {showCondResolve && (
        <StandardModal
          title="Resolve Outstanding Conditions"
          onClose={() => { setShowCondResolve(false); setResolveItems([]); setResolveNotes(""); }}
          width={540}
          footer={
            <>
              <Btn variant="secondary" onClick={() => { setShowCondResolve(false); setResolveItems([]); setResolveNotes(""); }}>Cancel</Btn>
              <Btn variant="success" type="submit" form="resolve-form" loading={saving}>
                Mark Selected Resolved
              </Btn>
            </>
          }
        >
          <form id="resolve-form" onSubmit={submitResolveCondition}>
            <div style={{ marginBottom: spacing.md, fontSize: typography.fontSizes.sm, color: colors.textSecondary }}>
              Select the conditions that have been satisfied. If all conditions are resolved,
              the accreditation status will automatically promote to <strong>Approved</strong>.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs, marginBottom: spacing.md }}>
              {(accred.conditions || []).map((c, i) => (
                <label key={i} style={{
                  display: "flex", alignItems: "center", gap: spacing.sm,
                  background: resolveItems.includes(c) ? colors.successLight : colors.backgroundAlt,
                  border: `1px solid ${resolveItems.includes(c) ? colors.success : colors.border}`,
                  borderRadius: radius.md, padding: `${spacing.sm} ${spacing.md}`,
                  cursor: "pointer",
                }}>
                  <input
                    type="checkbox"
                    checked={resolveItems.includes(c)}
                    onChange={() => toggleResolveItem(c)}
                    style={{ accentColor: colors.success }}
                  />
                  <span style={{ fontSize: typography.fontSizes.sm }}>{c}</span>
                </label>
              ))}
            </div>
            <FormField label="Resolution Notes" htmlFor="resolve-notes"
              helper="Document the evidence of compliance reviewed.">
              <textarea
                id="resolve-notes"
                className="ds-input"
                value={resolveNotes}
                onChange={e => setResolveNotes(e.target.value)}
                rows={3}
                placeholder="Describe how each condition was evidenced and confirmed satisfied…"
                style={textareaStyle}
              />
            </FormField>
          </form>
        </StandardModal>
      )}
    </div>
  );
}
